import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import {
  Button,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  ListSubheader,
  Typography,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Box,
  Collapse,
  Divider,
  CircularProgress,
} from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import AddIcon from '@mui/icons-material/Add'
import CloseIcon from '@mui/icons-material/Close'
import ShareIcon from '@mui/icons-material/Share'
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined'
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline'
import { TourProvider } from '@reactour/tour'
import { useAppStore } from '@/store/appStore'
import {
  acceptCustomView,
  createCustomView,
  updateCustomView,
  deleteCustomView,
  leaveCustomViewShare,
} from '@/api/customViewsApi'
import { useQueryClient } from '@tanstack/react-query'
import { refreshSharingState, reloadCustomViews } from '@/services/customViewsSync'
import { extractApiErrorCode, extractApiErrorStatus } from '@/utils/apiError'
import { canEditView, isOwnedView } from '@/utils/customViews'
import { CustomViewShareDialog } from './CustomViewShareDialog'
import { useSharedCustomViewsEnabled } from '@/hooks/useSharedCustomViewsEnabled'
import { useCustomViewTour } from '@/hooks/useCustomViewTour'
import { TourContentComponent } from '@/components/tour/ProductTour'
import {
  CustomViewEditorForm,
  CustomViewTourController,
  CustomViewTourHelpButton,
} from './CustomViewEditorDialog'
import type { ApiCustomView, ApiPendingCustomView, CustomViewMutationRequest } from '@/types/api'

// A 403/404 on a view the caller could see means the owner revoked access or deleted it meanwhile.
function isAccessLost(err: unknown): boolean {
  const status = extractApiErrorStatus(err)
  if (status === 404) return true
  return status === 403 && extractApiErrorCode(err) !== 'CustomViewReadOnly'
}

export function CustomViewsManager() {
  const { t } = useTranslation()
  const { customViews, pendingCustomViews, isOffline } = useAppStore()
  const { hasSeen, markSeen } = useCustomViewTour()
  const sharingEnabled = useSharedCustomViewsEnabled()
  const queryClient = useQueryClient()

  const hasViews = customViews.length > 0
  const ownedViews = customViews.filter(isOwnedView)
  const sharedViews = sharingEnabled ? customViews.filter((cv) => !isOwnedView(cv)) : []
  const pendingViews = sharingEnabled ? pendingCustomViews : []

  // Form is open by default when there are no views; otherwise starts closed
  const [formOpen, setFormOpen] = useState(!hasViews)
  const [editTarget, setEditTarget] = useState<ApiCustomView | null>(null)

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<ApiCustomView | null>(null)

  const [isSaving, setIsSaving] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  const [shareTarget, setShareTarget] = useState<ApiCustomView | null>(null)
  const [leaveTarget, setLeaveTarget] = useState<ApiCustomView | null>(null)
  const [isLeaving, setIsLeaving] = useState(false)
  const [respondingId, setRespondingId] = useState<number | null>(null)

  useEffect(() => {
    if (isOffline && formOpen) closeForm()
  }, [isOffline])

  // Show the tour automatically when: no views exist AND the user hasn't seen it
  const shouldAutoLaunchTour = !hasViews && !hasSeen()

  const reload = reloadCustomViews

  const isEditMode = editTarget !== null

  const openCreateForm = () => {
    setEditTarget(null)
    setFormOpen(true)
  }

  const openEditForm = (cv: ApiCustomView) => {
    setEditTarget(cv)
    setFormOpen(true)
  }

  const closeForm = () => {
    setFormOpen(false)
    // editTarget is cleared in onExited so the form doesn't reset while collapsing
  }

  const handleEditorSave = async (payload: CustomViewMutationRequest) => {
    const isEdit = !!editTarget
    setIsSaving(true)
    try {
      if (isEdit) {
        await updateCustomView(editTarget!.id, { ...payload, expectedUpdatedOn: editTarget!.updatedOn ?? null })
      } else {
        await createCustomView(payload)
      }
      await reload()
      toast.success(isEdit ? t('customViews.updated') : t('customViews.created'))
    } catch (err) {
      if (isEdit && extractApiErrorCode(err) === 'CustomViewConcurrencyConflict') {
        toast.error(t('customViews.concurrencyConflict'))
        closeForm()
        await reload().catch(() => undefined)
        return
      }
      if (isEdit && isAccessLost(err)) {
        toast.error(t('customViews.accessRevoked'))
        closeForm()
        await refreshSharingState(queryClient)
        return
      }
      toast.error(isEdit ? t('customViews.updateFailed') : t('customViews.createFailed'))
      throw err // re-throw so the form shows the error and stays open
    } finally {
      setIsSaving(false)
    }
  }

  const handleDeleteClick = (cv: ApiCustomView) => {
    setDeleteTarget(cv)
    setDeleteConfirmOpen(true)
  }

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return
    setIsDeleting(true)
    try {
      await deleteCustomView(deleteTarget.id)
      setDeleteConfirmOpen(false)
      setDeleteTarget(null)
      await reload()
      toast.success(t('customViews.deletedSuccessfully'))
    } catch {
      toast.error(t('customViews.deleteFailed'))
    } finally {
      setIsDeleting(false)
    }
  }

  const handleLeaveConfirm = async () => {
    if (!leaveTarget) return
    setIsLeaving(true)
    try {
      await leaveCustomViewShare(leaveTarget.id)
      toast.success(t('customViews.left'))
    } catch (err) {
      if (!isAccessLost(err)) toast.error(t('customViews.leaveFailed'))
    } finally {
      setLeaveTarget(null)
      setIsLeaving(false)
      await refreshSharingState(queryClient)
    }
  }

  const handlePendingResponse = async (pv: ApiPendingCustomView, accept: boolean) => {
    setRespondingId(pv.id)
    try {
      if (accept) await acceptCustomView(pv.id)
      else await leaveCustomViewShare(pv.id)
      toast.success(accept ? t('customViews.accepted') : t('customViews.declined'))
    } catch (err) {
      if (!isAccessLost(err)) toast.error(accept ? t('customViews.acceptFailed') : t('customViews.declineFailed'))
    } finally {
      setRespondingId(null)
      await refreshSharingState(queryClient)
    }
  }

  const renderPendingRow = (pv: ApiPendingCustomView) => {
    const busy = respondingId === pv.id
    const detail = `${t('customViews.pendingFrom', { owner: pv.ownerUsername ?? '' })} · ${pv.canEdit ? t('customViews.canEdit') : t('customViews.viewOnly')}`
    return (
      <ListItem key={pv.id} divider sx={{ pr: 24 }}>
        <ListItemText
          primary={
            <Typography variant="body1" fontWeight={600}>
              {pv.name}
            </Typography>
          }
          secondary={detail}
        />
        <ListItemSecondaryAction sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title={isOffline ? t('customViews.offlineDisabled') : ''}>
            <span>
              <Button
                size="small"
                onClick={() => handlePendingResponse(pv, false)}
                disabled={isOffline || respondingId !== null}
              >
                {t('customViews.decline')}
              </Button>
            </span>
          </Tooltip>
          <Tooltip title={isOffline ? t('customViews.offlineDisabled') : ''}>
            <span>
              <Button
                size="small"
                variant="contained"
                onClick={() => handlePendingResponse(pv, true)}
                disabled={isOffline || respondingId !== null}
                startIcon={busy ? <CircularProgress size={16} color="inherit" /> : undefined}
              >
                {t('customViews.accept')}
              </Button>
            </span>
          </Tooltip>
        </ListItemSecondaryAction>
      </ListItem>
    )
  }

  const renderOwnedRow = (cv: ApiCustomView) => (
    <ListItem key={cv.id} divider sx={{ pr: sharingEnabled ? 20 : 16 }}>
      <ListItemText
        primary={
          <Typography variant="body1" fontWeight={600}>
            {cv.name}
          </Typography>
        }
        secondary={
          sharingEnabled && (cv.shareCount ?? 0) > 0
            ? `${t('customViews.practiceAreaCount', { count: cv.practiceAreas.length })} · ${t('customViews.sharedWithCount', { count: cv.shareCount })}`
            : t('customViews.practiceAreaCount', { count: cv.practiceAreas.length })
        }
      />
      <ListItemSecondaryAction>
        {sharingEnabled && (
          <Tooltip title={isOffline ? t('customViews.offlineDisabled') : t('customViews.shareView')}>
            <span>
              <IconButton
                edge="end"
                size="small"
                onClick={() => setShareTarget(cv)}
                disabled={isOffline}
                aria-label={t('customViews.shareView')}
              >
                <ShareIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
        <Tooltip title={isOffline ? t('customViews.offlineDisabled') : t('customViews.editView')}>
          <span>
            <IconButton
              edge="end"
              size="small"
              onClick={() => openEditForm(cv)}
              disabled={isOffline}
              aria-label={t('customViews.editView')}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={isOffline ? t('customViews.offlineDisabled') : t('customViews.deleteView')}>
          <span>
            <IconButton
              edge="end"
              size="small"
              onClick={() => handleDeleteClick(cv)}
              disabled={isOffline}
              aria-label={t('customViews.deleteView')}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </ListItemSecondaryAction>
    </ListItem>
  )

  const renderSharedRow = (cv: ApiCustomView) => {
    const ownedBy = t('customViews.ownedBy', { owner: cv.ownerUsername ?? '' })
    const editable = canEditView(cv)
    return (
      <ListItem key={cv.id} divider sx={{ pr: 16 }}>
        <ListItemText
          primary={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Tooltip title={ownedBy}>
                <PeopleAltOutlinedIcon fontSize="small" color="action" aria-label={ownedBy} />
              </Tooltip>
              <Typography variant="body1" fontWeight={600}>
                {cv.name}
              </Typography>
            </Box>
          }
          secondary={`${ownedBy} · ${editable ? t('customViews.canEdit') : t('customViews.viewOnly')}`}
        />
        <ListItemSecondaryAction>
          {editable && (
            <Tooltip title={isOffline ? t('customViews.offlineDisabled') : t('customViews.editView')}>
              <span>
                <IconButton
                  edge="end"
                  size="small"
                  onClick={() => openEditForm(cv)}
                  disabled={isOffline}
                  aria-label={t('customViews.editView')}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}
          <Tooltip title={isOffline ? t('customViews.offlineDisabled') : t('customViews.leaveView')}>
            <span>
              <IconButton
                edge="end"
                size="small"
                onClick={() => setLeaveTarget(cv)}
                disabled={isOffline}
                aria-label={t('customViews.leaveView')}
              >
                <RemoveCircleOutlineIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </ListItemSecondaryAction>
      </ListItem>
    )
  }

  return (
    <TourProvider
      steps={[]}
      components={{ Content: TourContentComponent }}
      onClickClose={({ setIsOpen }) => { markSeen(); setIsOpen(false) }}
      beforeClose={() => markSeen()}
      styles={{
        popover: (base) => ({
          ...base,
          borderRadius: 10,
          padding: '20px 24px',
          maxWidth: 320,
          boxShadow: 'var(--shadow-overlay-strong)',
        }),
        maskArea: (base) => ({ ...base, rx: 6 }),
      }}
      padding={{ mask: 8, popover: [10, 12] }}
      showBadge={false}
    >
      <CustomViewTourController
        shouldAutoLaunch={shouldAutoLaunchTour}
        isEditMode={isEditMode}
      />

      <Box>
        {/* Form panel — unfurls from top */}
        <Collapse in={formOpen} unmountOnExit onExited={() => setEditTarget(null)}>
          <Box
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              p: 2,
              mb: hasViews ? 2 : 0,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Box>
                <Typography variant="subtitle1" fontWeight={600}>
                  {isEditMode ? t('customViews.editView') : t('customViews.createNew')}
                </Typography>
                {editTarget && !isOwnedView(editTarget) && (
                  <Typography variant="body2" color="text.secondary">
                    {t('customViews.ownedBy', { owner: editTarget.ownerUsername ?? '' })}
                  </Typography>
                )}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CustomViewTourHelpButton isEditMode={isEditMode} />
                <Tooltip title={t('notes.cancel')}>
                  <IconButton size="small" onClick={closeForm} aria-label={t('notes.cancel')}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            <CustomViewEditorForm
              key={editTarget?.id ?? 'create'}
              editTarget={editTarget}
              onClose={closeForm}
              onSave={handleEditorSave}
            />
          </Box>

          {hasViews && <Divider sx={{ mb: 2 }} />}
        </Collapse>

        {pendingViews.length > 0 && (
          <List
            disablePadding
            sx={{ mb: 2 }}
            subheader={
              <ListSubheader disableSticky sx={{ px: 0, lineHeight: 1.5, pb: 1 }}>
                {t('customViews.awaitingAcceptance')}
                <Typography variant="body2" color="text.secondary">
                  {t('customViews.awaitingAcceptanceHelp')}
                </Typography>
              </ListSubheader>
            }
          >
            {pendingViews.map(renderPendingRow)}
          </List>
        )}

        {/* List + toolbar */}
        {hasViews ? (
          <Box>
            {!formOpen && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                <Tooltip title={isOffline ? t('customViews.offlineDisabled') : ''}>
                  <span>
                    <Button
                      variant="contained"
                      startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
                      onClick={openCreateForm}
                      size="small"
                      disabled={isSaving || isOffline}
                    >
                      {t('customViews.createNew')}
                    </Button>
                  </span>
                </Tooltip>
              </Box>
            )}

            {ownedViews.length > 0 && (
              <List
                disablePadding
                subheader={
                  sharedViews.length > 0 ? (
                    <ListSubheader disableSticky sx={{ px: 0 }}>{t('customViews.myViews')}</ListSubheader>
                  ) : undefined
                }
              >
                {ownedViews.map(renderOwnedRow)}
              </List>
            )}

            {sharedViews.length > 0 && (
              <List
                disablePadding
                subheader={
                  <ListSubheader disableSticky sx={{ px: 0, mt: ownedViews.length > 0 ? 2 : 0 }}>
                    {t('customViews.sharedWithMe')}
                  </ListSubheader>
                }
              >
                {sharedViews.map(renderSharedRow)}
              </List>
            )}
          </Box>
        ) : (
          !formOpen && (
            <Box sx={{ pt: 3, textAlign: 'center' }}>
              <Typography color="text.secondary" sx={{ mb: 2 }}>
                {t('customViews.noViewsYet')}
              </Typography>
              <Tooltip title={isOffline ? t('customViews.offlineDisabled') : ''}>
                <span>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={openCreateForm}
                    disabled={isOffline}
                  >
                    {t('customViews.createNew')}
                  </Button>
                </span>
              </Tooltip>
            </Box>
          )
        )}

        {sharingEnabled && shareTarget && (
          <CustomViewShareDialog
            key={shareTarget.id}
            view={customViews.find((cv) => cv.id === shareTarget.id) ?? shareTarget}
            open
            onClose={() => setShareTarget(null)}
          />
        )}

        <Dialog
          open={leaveTarget !== null}
          onClose={() => !isLeaving && setLeaveTarget(null)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>{t('customViews.leaveView')}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {t('customViews.confirmLeave', {
                name: leaveTarget?.name ?? '',
                owner: leaveTarget?.ownerUsername ?? '',
              })}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setLeaveTarget(null)} disabled={isLeaving}>
              {t('notes.cancel')}
            </Button>
            <Button
              color="error"
              variant="contained"
              onClick={handleLeaveConfirm}
              disabled={isLeaving}
              startIcon={isLeaving ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              {t('customViews.leaveView')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete confirmation dialog */}
        <Dialog
          open={deleteConfirmOpen}
          onClose={() => !isDeleting && setDeleteConfirmOpen(false)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>{t('customViews.deleteView')}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {t('customViews.confirmDelete', { name: deleteTarget?.name ?? '' })}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteConfirmOpen(false)} disabled={isDeleting}>
              {t('notes.cancel')}
            </Button>
            <Button
              color="error"
              variant="contained"
              onClick={handleDeleteConfirm}
              disabled={isDeleting}
              startIcon={isDeleting ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              {isDeleting ? t('notes.saving') : t('customViews.deleteView')}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </TourProvider>
  )
}
